import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { CompanyCustomer } from "model/Company";
import { useMemo, useState } from "react";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useTranslation } from "react-i18next";
import {
  CustomerSection,
  ServiceFormActions,
  ServiceFormContent,
  ServiceFormField,
} from "./customerPanel.styled";

type Props = {
  isSaving: boolean;
  setShowAddCustomerForm: React.Dispatch<React.SetStateAction<boolean>>;
  showAddCustomerForm: boolean;
  saveCustomer(customer: CompanyCustomer): Promise<boolean>;
};

export const AddCustomerForm: React.FC<Props> = ({
  isSaving,
  setShowAddCustomerForm,
  showAddCustomerForm,
  saveCustomer,
}) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const [customerName, setCustomerName] = useState("");
  const [customerOrgNr, setCustomerOrgNr] = useState("");
  const [customerContact, setCustomerContact] = useState("");

  const withMandatory = (label: string): string =>
    `${label} (${t("Staff.AddStaffPanelMandatory")})`;

  const isSaveDisabled = useMemo(
    (): boolean =>
      !customerName.trim() || !customerOrgNr.trim() || !customerContact.trim(),
    [customerContact, customerName, customerOrgNr],
  );

  const resetForm = (): void => {
    setCustomerName("");
    setCustomerOrgNr("");
    setCustomerContact("");
  };

  const onClose = (): void => {
    resetForm();
    setShowAddCustomerForm(false);
  };

  const handleSave = async (): Promise<void> => {
    const companyId = companyState.company?.id;
    if (!companyId) return;

    const trimmedCustomerName = customerName.trim();
    const trimmedCustomerOrgNr = customerOrgNr.trim();
    const trimmedCustomerContact = customerContact.trim();

    const wasSaved = await saveCustomer({
      companyId,
      name: trimmedCustomerName,
      organizationNumber: trimmedCustomerOrgNr,
      contact: trimmedCustomerContact,
    });

    if (wasSaved) {
      resetForm();
      setShowAddCustomerForm(false);
    }
  };

  return (
    <Modal
      isOpen={showAddCustomerForm}
      onClose={onClose}
      label={t("CompanyCustomers.AddCustomer")}
      size="small"
      footer={
        <ServiceFormActions>
          <SecondaryButton
            label={t("General.Close")}
            onClick={onClose}
            disabled={isSaving}
            size="small"
            type="button"
          />
          <PrimaryButton
            label={t("General.Save")}
            onClick={() => {
              void handleSave();
            }}
            disabled={isSaveDisabled || isSaving}
            isLoading={isSaving}
            size="small"
            type="button"
          />
        </ServiceFormActions>
      }
    >
      <ServiceFormContent>
        <CustomerSection>
          <ServiceFormField>
            <InputField
              label={withMandatory(t("CompanyCustomers.Name"))}
              value={customerName}
              onChange={setCustomerName}
              onClear={() => setCustomerName("")}
              fullWidth
            />
          </ServiceFormField>

          <ServiceFormField>
            <InputField
              label={withMandatory(t("CompanyCustomers.OrganizationNumber"))}
              value={customerOrgNr}
              onChange={setCustomerOrgNr}
              onClear={() => setCustomerOrgNr("")}
              fullWidth
            />
          </ServiceFormField>

          <ServiceFormField>
            <InputField
              label={withMandatory(t("CompanyCustomers.Contact"))}
              value={customerContact}
              onChange={setCustomerContact}
              onClear={() => setCustomerContact("")}
              fullWidth
            />
          </ServiceFormField>
        </CustomerSection>
      </ServiceFormContent>
    </Modal>
  );
};
